/**
 * Text measurement seam. UI-1 ships a heuristic monospace measurer (tests + pre-MSDF fallback);
 * UI-3 replaces it with the real MSDF glyph layouter over font-asset metrics.
 */
import { UIMeasureMode } from '../layout/ILayoutEngine';
import type { FontStyleValue, TextAlign, WhiteSpaceValue } from '../types';

export interface UITextStyle {
  /** Font asset guid ('' = engine default). Wins over `fontFamily` when set. */
  font: string;
  /** Family-name stack (resolved by weight/style); '' font + this drives family resolution. */
  fontFamily: readonly string[];
  fontWeight: number;
  fontStyle: FontStyleValue;
  fontSize: number;
  /** Multiplier of fontSize. */
  lineHeight: number;
  letterSpacing: number;
  textAlign: TextAlign;
  whiteSpace: WhiteSpaceValue;
}

export const DEFAULT_TEXT_STYLE: UITextStyle = {
  font: '',
  fontFamily: [],
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: 14,
  lineHeight: 1.2,
  letterSpacing: 0,
  textAlign: 'left',
  whiteSpace: 'normal',
};

export interface ITextMeasurer {
  measure(
    text: string,
    style: UITextStyle,
    availableWidth: number,
    widthMode: UIMeasureMode,
  ): { width: number; height: number };
}

/** Heuristic measurer: fixed advance = 0.6 × fontSize, greedy character wrap. */
export class MonospaceTextMeasurer implements ITextMeasurer {
  constructor(private readonly advanceFactor = 0.6) {}

  measure(
    text: string,
    style: UITextStyle,
    availableWidth: number,
    widthMode: UIMeasureMode,
  ): { width: number; height: number } {
    const advance = style.fontSize * this.advanceFactor + style.letterSpacing;
    const lineHeight = style.fontSize * style.lineHeight;
    const intrinsic = text.length * advance;
    if (
      style.whiteSpace === 'nowrap' ||
      widthMode === UIMeasureMode.Undefined ||
      availableWidth <= 0 ||
      intrinsic <= availableWidth
    ) {
      return { width: intrinsic, height: text.length ? lineHeight : 0 };
    }
    const perLine = Math.max(1, Math.floor(availableWidth / advance));
    const lines = Math.ceil(text.length / perLine);
    return { width: Math.min(intrinsic, perLine * advance), height: lines * lineHeight };
  }
}
